import { useNavigate } from "react-router-dom";
import { PlatformLogo } from "@/components/PlatformLogo";
import heroImg from "@/assets/hero-dataflow.jpg";
import {
  Database, ArrowRight, Cube, GitBranch, ArrowsClockwise, Clock,
  ShieldCheck, ChartLineUp, CheckCircle, Lightning, Lock, Trash,
} from "@phosphor-icons/react";

const HERO_IMG = heroImg;

const CONNECTORS = [
  { platform: "salesforce", label: "Salesforce" },
  { platform: "hubspot", label: "HubSpot" },
  { platform: "zendesk", label: "Zendesk" },
  { platform: "pipedrive", label: "Pipedrive" },
  { platform: "google_sheets", label: "Google Sheets" },
  { platform: "postgresql", label: "PostgreSQL", baseUrl: "https://postgresql.org" },
  { platform: "mysql", label: "MySQL" },
];

const CAPS = [
  { icon: Cube, title: "100+ connectors, no code", body: "Add Salesforce, HubSpot, Zendesk, Pipedrive, Google Sheets, SQL databases and any REST API through the declarative Connector Studio — config, not code." },
  { icon: GitBranch, title: "Guided 8-step wizard", body: "Discover objects & fields, auto-suggested mappings, transform rules, validation, a 10-record test run, then the full migration — all point-and-click." },
  { icon: ArrowsClockwise, title: "Insert, Update & Upsert", body: "Choose a write mode per object and a match field. Re-runs update existing records instead of duplicating them, with case-insensitive field matching." },
  { icon: Clock, title: "Scheduled recurring syncs", body: "Keep systems aligned automatically — hourly, every 6 hours, daily or weekly. Already-migrated records are skipped for safe, idempotent re-runs." },
  { icon: ShieldCheck, title: "Enterprise security", body: "Encrypted credentials at rest, strict multi-tenant isolation at the data layer, and tokens that are never exposed to the browser." },
  { icon: ChartLineUp, title: "Monitoring & reconciliation", body: "Live job progress with pause, resume and retry. Downloadable error reports and a records explorer to trace every source → target row." },
];

const STEPS = [
  { n: "01", title: "Connect", body: "Add encrypted source & destination connections and test them in one click." },
  { n: "02", title: "Map", body: "Match objects and fields with smart auto-suggestions, filters and transforms." },
  { n: "03", title: "Validate & test", body: "Run pre-flight validation and a small sample migration before committing." },
  { n: "04", title: "Migrate & monitor", body: "Launch in the background, watch progress, and reconcile results." },
];

function Nav({ go }) {
  return (
    <nav className="fixed top-0 inset-x-0 z-50 h-16 backdrop-blur-md bg-[#1C1917]/80 border-b border-white/5">
      <div className="max-w-7xl mx-auto h-full px-6 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-xl bg-[#C2410C] flex items-center justify-center shadow-glow">
            <Database size={18} weight="fill" className="text-white" />
          </span>
          <span className="font-head font-semibold text-[15px] tracking-tight text-white">Inspiro Interface</span>
        </div>
        <div className="hidden md:flex items-center gap-8 text-sm text-zinc-300">
          <a href="#capabilities" data-testid="nav-link-capabilities" className="hover:text-white transition-colors duration-200">Capabilities</a>
          <a href="#how" data-testid="nav-link-how" className="hover:text-white transition-colors duration-200">How it works</a>
          <a href="#connectors" data-testid="nav-link-connectors" className="hover:text-white transition-colors duration-200">Connectors</a>
          <a href="#data" data-testid="nav-link-data" className="hover:text-white transition-colors duration-200">Security</a>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="landing-nav-signin" onClick={() => go("login")}
            className="text-sm text-zinc-200 hover:text-white transition-colors duration-200">Sign in</button>
          <button data-testid="landing-nav-signup" onClick={() => go("register")}
            className="rounded-full bg-[#C2410C] hover:bg-[#9A3412] text-white text-sm font-medium px-5 py-2 transition-all duration-200 hover:-translate-y-px shadow-glow">
            Create organization
          </button>
        </div>
      </div>
    </nav>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const go = (mode) => navigate(mode === "register" ? "/login?mode=register" : "/login");

  return (
    <div className="min-h-screen bg-[#FAF9F7] text-stone-900">
      <Nav go={go} />

      {/* HERO */}
      <header className="relative brand-ink overflow-hidden pt-16">
        <div className="absolute inset-0 brand-grid pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-10 items-center min-h-[86vh] py-20">
          <div className="animate-fadeup">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium tracking-wide text-zinc-200">
              <Lightning size={13} weight="fill" className="text-[#F97316]" /> Multi-tenant data migration & integration
            </span>
            <h1 className="font-head font-bold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] mt-6">
              Move & sync your data<br />between any system —<br /><span className="text-[#F97316]">without the mess.</span>
            </h1>
            <p className="text-zinc-300 text-base sm:text-lg leading-relaxed mt-6 max-w-xl">
              A connector-based platform to discover, map, transform, validate and migrate records
              across Salesforce, HubSpot, Google Sheets, SQL databases and 100+ REST APIs — with
              full tenant isolation and audit trails. No engineering required.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-9">
              <button data-testid="hero-cta-signup" onClick={() => go("register")}
                className="rounded-full bg-[#C2410C] hover:bg-[#9A3412] text-white font-medium px-7 py-3 transition-all duration-200 hover:-translate-y-px shadow-glow inline-flex items-center gap-2">
                Start free <ArrowRight size={17} weight="bold" />
              </button>
              <button data-testid="hero-cta-signin" onClick={() => go("login")}
                className="rounded-xl bg-white/10 border border-white/20 hover:bg-white/20 text-white font-medium px-7 py-3 transition-colors duration-200 backdrop-blur-md">
                Sign in
              </button>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-10 text-xs uppercase tracking-[0.16em] text-zinc-400">
              <span className="flex items-center gap-1.5"><CheckCircle size={13} weight="fill" className="text-emerald-500" /> Encrypted</span>
              <span className="flex items-center gap-1.5"><CheckCircle size={13} weight="fill" className="text-emerald-500" /> Multi-tenant</span>
              <span className="flex items-center gap-1.5"><CheckCircle size={13} weight="fill" className="text-emerald-500" /> Auditable</span>
            </div>
          </div>
          <div className="relative hidden lg:block">
            <img src={HERO_IMG} alt="Abstract data flow network"
              className="w-full h-auto mix-blend-screen opacity-95 select-none pointer-events-none"
              style={{ maskImage: "radial-gradient(75% 70% at 55% 50%, black 30%, transparent 82%)", WebkitMaskImage: "radial-gradient(75% 70% at 55% 50%, black 30%, transparent 82%)" }} />
          </div>
        </div>
      </header>

      {/* CONNECTOR LOGO WALL */}
      <section id="connectors" className="border-b border-stone-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <p className="text-center text-xs uppercase tracking-[0.2em] text-stone-400 font-semibold">Connect the tools your business runs on</p>
          <div className="flex flex-wrap items-center justify-center gap-5 sm:gap-8 mt-7">
            {CONNECTORS.map((c) => (
              <div key={c.platform} className="flex items-center gap-2.5 opacity-90 hover:opacity-100 transition-opacity duration-200">
                <PlatformLogo platform={c.platform} size={22} baseUrl={c.baseUrl} />
                <span className="text-sm font-medium text-stone-600">{c.label}</span>
              </div>
            ))}
            <div className="flex items-center gap-2 rounded-full bg-stone-100 border border-stone-200 px-4 py-1.5">
              <Cube size={16} className="text-[#C2410C]" weight="fill" />
              <span className="text-sm font-medium text-stone-700">100+ more via Connector Studio</span>
            </div>
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section id="capabilities" className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-[#C2410C]">Capabilities</span>
            <h2 className="font-head font-bold text-3xl sm:text-4xl tracking-tight text-stone-900 mt-3">
              Everything you need to move data with confidence
            </h2>
            <p className="text-stone-600 text-base leading-relaxed mt-4">
              Purpose-built for real migrations — from a one-off CRM consolidation to ongoing
              scheduled synchronization across your stack.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {CAPS.map((c, i) => (
              <div key={c.title} data-testid={`capability-${i}`}
                className="animate-fadeup bg-white border border-stone-200 rounded-2xl shadow-card p-7 hover:-translate-y-1 hover:shadow-float transition-all duration-300"
                style={{ animationDelay: `${i * 60}ms` }}>
                <span className="w-11 h-11 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center">
                  <c.icon size={22} weight="duotone" className="text-[#C2410C]" />
                </span>
                <h3 className="font-head font-semibold text-lg text-stone-900 mt-5">{c.title}</h3>
                <p className="text-sm text-stone-600 leading-relaxed mt-2">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-24 bg-white border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-[#C2410C]">How it works</span>
            <h2 className="font-head font-bold text-3xl sm:text-4xl tracking-tight text-stone-900 mt-3">
              From connected to migrated in four steps
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
            {STEPS.map((s, i) => (
              <div key={s.n} className="relative">
                <div className="font-head font-bold text-5xl text-stone-200 stat-num">{s.n}</div>
                <div className="h-px w-12 bg-[#C2410C] my-4" />
                <h3 className="font-head font-semibold text-lg text-stone-900">{s.title}</h3>
                <p className="text-sm text-stone-600 leading-relaxed mt-2">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DATA RESIDENCY / TRUST */}
      <section id="data" className="py-24">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
          <div>
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-[#C2410C]">Data handling</span>
            <h2 className="font-head font-bold text-3xl sm:text-4xl tracking-tight text-stone-900 mt-3">
              Your data stays yours
            </h2>
            <p className="text-stone-600 text-base leading-relaxed mt-4 max-w-xl">
              Inspiro streams records directly from source to destination — we don't warehouse your
              CRM or database records. We keep only migration metadata for monitoring and reconciliation.
            </p>
            <ul className="mt-8 space-y-5">
              {[
                { icon: ArrowsClockwise, title: "Streamed pass-through", body: "Records move source → destination in memory during the run — not into our database." },
                { icon: Database, title: "Metadata only", body: "We persist record IDs, status and your field mappings — not your record field values." },
                { icon: Trash, title: "You're in control", body: "Files you upload (CSV / Excel) are stored solely to run the migration and are removed when you delete the connection." },
                { icon: ShieldCheck, title: "Encrypted & isolated", body: "Credentials are encrypted at rest with strict per-tenant isolation and a full audit trail." },
              ].map((p) => (
                <li key={p.title} className="flex gap-4">
                  <span className="shrink-0 w-10 h-10 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center">
                    <p.icon size={20} weight="duotone" className="text-[#C2410C]" />
                  </span>
                  <div>
                    <div className="font-head font-semibold text-stone-900">{p.title}</div>
                    <div className="text-sm text-stone-600 leading-relaxed mt-0.5">{p.body}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative brand-ink rounded-3xl border border-white/5 shadow-float overflow-hidden p-10 min-h-[320px] flex flex-col items-center justify-center text-center">
            <div className="absolute inset-0 brand-grid pointer-events-none" />
            <span className="relative w-16 h-16 rounded-2xl bg-[#C2410C] flex items-center justify-center shadow-glow">
              <Lock size={30} weight="fill" className="text-white" />
            </span>
            <h3 className="relative font-head font-semibold text-white text-xl mt-6">Private by design</h3>
            <p className="relative text-zinc-300 text-sm leading-relaxed mt-3 max-w-xs">
              No record warehousing. Encrypted credentials. Tenant-isolated. Auditable end to end.
            </p>
            <div className="relative flex flex-wrap justify-center gap-2 mt-6">
              {["Encrypted", "Tenant-isolated", "Auditable", "No warehousing"].map((t) => (
                <span key={t} className="rounded-full bg-white/10 border border-white/15 text-zinc-200 text-xs px-3 py-1">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative brand-ink overflow-hidden">
        <div className="absolute inset-0 brand-grid pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 py-24 text-center">
          <h2 className="font-head font-bold text-white text-3xl sm:text-5xl tracking-tight leading-tight">
            Start migrating in minutes
          </h2>
          <p className="text-zinc-300 text-base sm:text-lg mt-5 max-w-2xl mx-auto">
            Spin up a workspace, connect a demo source and destination, and run your first
            migration with the built-in Mock connectors — no external accounts needed.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-9">
            <button data-testid="final-cta-signup" onClick={() => go("register")}
              className="rounded-full bg-[#C2410C] hover:bg-[#9A3412] text-white font-medium px-8 py-3.5 transition-all duration-200 hover:-translate-y-px shadow-glow inline-flex items-center gap-2">
              Create your organization <ArrowRight size={17} weight="bold" />
            </button>
            <button data-testid="final-cta-signin" onClick={() => go("login")}
              className="rounded-xl bg-white/10 border border-white/20 hover:bg-white/20 text-white font-medium px-8 py-3.5 transition-colors duration-200 backdrop-blur-md">
              Sign in
            </button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#0C0A09] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <span className="w-7 h-7 rounded-lg bg-[#C2410C] flex items-center justify-center">
              <Database size={15} weight="fill" className="text-white" />
            </span>
            <span className="font-head font-semibold text-sm text-white">Inspiro Interface</span>
          </div>
          <div className="text-xs text-zinc-500">© 2026 Inspiro Interface — Multi-Tenant Data Migration Platform</div>
        </div>
      </footer>
    </div>
  );
}
